import { Fragment, useState } from 'react'
import type { GccCapabilitySequencing, GccPathway, GccStage3Result, GccTaskSequencing, GccWave } from '@shared/types'
import { GCC_PATHWAYS, GCC_PATHWAY_LABELS, GCC_WAVES, GCC_WAVE_BANDS, formatSourcingPair } from '@tn/shared'
import { GccTooltip, TipEnum } from './GccTooltip'
import { GCC_TOOLTIPS } from './gcc-tooltips'
import { PATHWAY_META, VERDICT_META, WAVE_META } from './gcc-meta'
import { Badge, Chip, fmtFte2 } from './GccSourcingModel'
import { GCC_BADGE } from './gccExecutiveStyle'
import type { GccBadgeStyle } from './gccExecutiveStyle'
import { QuickWinBadge } from './GccQuickWin'

// Badge colours as in the exported GCC dashboard (WI 2487)
const VERDICT_STYLE: Record<string, GccBadgeStyle> = {
  Now:   GCC_BADGE.red,
  Next:  GCC_BADGE.orange,
  Later: GCC_BADGE.yellow,
  Never: GCC_BADGE.gray,
}

const PATHWAY_STYLE: Record<GccPathway, GccBadgeStyle> = {
  TransformFirst:     GCC_BADGE.purple,
  ShiftFirst:         GCC_BADGE.blue,
  ShiftAndTransform:  GCC_BADGE.green,
  RetainAndTransform: GCC_BADGE.orange,
  ShiftOnly:          GCC_BADGE.yellow,
  RetainAsIs:         GCC_BADGE.gray,
}

/** True when a Step 3 result has the v2 task pathways and separate waves (WI 2325). */
export function isV2Sequencing(stage3: GccStage3Result | undefined): boolean {
  return !!stage3?.sequencing?.some(c => Array.isArray(c.pathwaySplit))
}

/** True when the capability matches the pathway (any part of its split), both wave filters and the mixed filter. */
export function matchesSequencingFilter(
  cap: GccCapabilitySequencing,
  pathway: GccPathway | null,
  transformWave: GccWave | null,
  migrationWave: GccWave | null,
  mixedOnly: boolean,
): boolean {
  if (mixedOnly && !cap.isMixed) return false
  if (pathway && !(cap.pathwaySplit ?? []).some(s => s.pathway === pathway)) return false
  if (transformWave && cap.transformWave !== transformWave) return false
  if (migrationWave && cap.migrationWave !== migrationWave) return false
  return true
}

function VerdictPill({ v }: { v: string }) {
  return <Badge label={v} style={VERDICT_STYLE[v]} />
}

function UrgencyDots({ score }: { score: number }) {
  return (
    <div className="flex items-center gap-1" aria-label={`Urgency ${score} of 3`}>
      {[1, 2, 3].map(n => {
        const filled = n <= score
        const bgColor = filled ? (score === 3 ? 'var(--bad)' : 'var(--c-msp)') : 'var(--line)'
        return <div key={n} className="w-2 h-2 rounded-full" style={{ background: bgColor }} />
      })}
    </div>
  )
}

function WavePill({ wave }: { wave: number | null | undefined }) {
  if (wave == null) return <span className="text-xs text-gray-300" aria-label="No wave">—</span>
  return (
    <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: GCC_BADGE.gray.bg, color: GCC_BADGE.gray.text }}
      title={`Wave ${wave} — ${GCC_WAVE_BANDS[wave as GccWave]?.label ?? ''}`}>
      {wave}
    </div>
  )
}

function PathwayPill({ pathway }: { pathway: GccPathway }) {
  return <Badge label={GCC_PATHWAY_LABELS[pathway] ?? pathway} style={PATHWAY_STYLE[pathway]} />
}

const URGENCY_DOTS: Record<number, string> = { 1: '●○○', 2: '●●○', 3: '●●●' }
const URGENCY_COLOR: Record<number, GccBadgeStyle> = {
  1: GCC_BADGE.gray,
  2: GCC_BADGE.yellow,
  3: GCC_BADGE.red,
}

/** v1 designs: LLM verdicts and one wave per capability. */
function LegacySequencing({ rows }: { rows: GccCapabilitySequencing[] }) {
  const [shiftFilter, setShiftFilter] = useState<string | null>(null)
  const [transformFilter, setTransformFilter] = useState<string | null>(null)
  const [urgencyFilter, setUrgencyFilter] = useState<number | null>(null)
  const [waveFilter, setWaveFilter] = useState<number | null>(null)
  const shifts = [...new Set(rows.map(r => r.shiftVerdict).filter(Boolean))]
  const transforms = [...new Set(rows.map(r => r.transformVerdict).filter(Boolean))]
  const filtered = rows.filter(r =>
    (!shiftFilter || r.shiftVerdict === shiftFilter) &&
    (!transformFilter || r.transformVerdict === transformFilter) &&
    (!urgencyFilter || r.urgencyScore === urgencyFilter) &&
    (!waveFilter || r.waveAssignment === waveFilter)
  )
  const hasFilter = !!shiftFilter || !!transformFilter || !!urgencyFilter || !!waveFilter

  return (
    <div>
      <div className="flex flex-wrap items-center gap-1.5 mb-4 pb-3 border-b border-gray-100 text-xs">
        <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mr-1">Shift</span>
        {shifts.map(v => (
          <Chip key={v} label={v} active={shiftFilter === v} style={VERDICT_STYLE[v]} onClick={() => setShiftFilter(p => p === v ? null : v)} />
        ))}
        <span className="text-gray-200 mx-0.5">|</span>
        <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mr-1">Transform</span>
        {transforms.map(v => (
          <Chip key={v} label={v} active={transformFilter === v} style={VERDICT_STYLE[v]} onClick={() => setTransformFilter(p => p === v ? null : v)} />
        ))}
        <span className="text-gray-200 mx-0.5">|</span>
        <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mr-1">Urgency</span>
        {([1, 2, 3] as const).map(u => (
          <Chip key={u} label={URGENCY_DOTS[u]} active={urgencyFilter === u} style={URGENCY_COLOR[u]} onClick={() => setUrgencyFilter(p => p === u ? null : u)} />
        ))}
        <span className="text-gray-200 mx-0.5">|</span>
        <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mr-1">Wave</span>
        {GCC_WAVES.map(w => (
          <Chip key={w} label={`W${w}`} active={waveFilter === w} style={GCC_BADGE.gray} onClick={() => setWaveFilter(p => p === w ? null : w)} />
        ))}
        {hasFilter && (
          <button onClick={() => { setShiftFilter(null); setTransformFilter(null); setUrgencyFilter(null); setWaveFilter(null) }} className="gx-link ml-1">Clear</button>
        )}
        <span className="ml-auto text-gray-400 text-[11px]">
          {hasFilter ? `${filtered.length} of ${rows.length}` : `${rows.length} rows`}
        </span>
      </div>
      <div className="gx-tbl-wrap">
        <table className="gx-tbl w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-400 border-b border-gray-100 uppercase tracking-wide">
              <th className="pb-3 font-medium">Capability</th>
              <th className="pb-3 font-medium w-24">
                <GccTooltip content={<>{GCC_TOOLTIPS.shiftVerdict}<TipEnum entries={VERDICT_META} /></>}>Shift</GccTooltip>
              </th>
              <th className="pb-3 font-medium w-24">
                <GccTooltip content={<>{GCC_TOOLTIPS.transformVerdict}<TipEnum entries={VERDICT_META} /></>}>Transform</GccTooltip>
              </th>
              <th className="pb-3 font-medium w-24">
                <GccTooltip content={GCC_TOOLTIPS.urgencyScore}>Urgency</GccTooltip>
              </th>
              <th className="pb-3 font-medium w-16">
                <GccTooltip content={<>{GCC_TOOLTIPS.waveAssignment}<TipEnum entries={WAVE_META} /></>}>Wave</GccTooltip>
              </th>
              <th className="pb-3 font-medium">Rationale</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filtered.map((seq, i) => (
              <tr key={i} className="hover:bg-gray-50 transition-colors">
                <td className="py-3 font-semibold text-gray-800 align-top">{seq.capabilityName}</td>
                <td className="py-3 align-top"><VerdictPill v={seq.shiftVerdict} /></td>
                <td className="py-3 align-top"><VerdictPill v={seq.transformVerdict} /></td>
                <td className="py-3 align-top"><UrgencyDots score={seq.urgencyScore} /></td>
                <td className="py-3 align-top"><WavePill wave={seq.waveAssignment} /></td>
                <td className="py-3 text-xs text-gray-500 leading-relaxed">{seq.rationale}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** Stacked bar of a capability's baseline FTE per pathway. */
function PathwayBar({ cap }: { cap: GccCapabilitySequencing }) {
  const split = cap.pathwaySplit ?? []
  if (split.length < 2) return null
  return (
    <div className="gx-bar mt-1.5 flex h-1.5 w-full rounded-full overflow-hidden bg-gray-100" role="img"
      aria-label={split.map(s => `${GCC_PATHWAY_LABELS[s.pathway]} ${s.sharePct}%`).join(', ')}>
      {split.map((s, i) => <div key={i} style={{ width: `${s.sharePct}%`, background: PATHWAY_STYLE[s.pathway].text }} />)}
    </div>
  )
}

/** v2 designs (WI 2325): task pathways rolled up per capability, with separate transformation and migration waves. */
function PathwaySequencing({ stage3, quickWinHorizonMonths }: { stage3: GccStage3Result; quickWinHorizonMonths?: number }) {
  const [pathwayFilter, setPathwayFilter] = useState<GccPathway | null>(null)
  const [transformFilter, setTransformFilter] = useState<GccWave | null>(null)
  const [migrationFilter, setMigrationFilter] = useState<GccWave | null>(null)
  const [mixedOnly, setMixedOnly] = useState(false)
  const [quickWinOnly, setQuickWinOnly] = useState(false)
  const [openRow, setOpenRow] = useState<number | null>(null)

  const rows = stage3.sequencing
  const tasks = stage3.taskSequencing ?? []
  const tasksByCapability = new Map<string, GccTaskSequencing[]>()
  for (const t of tasks) {
    const key = `${t.functionScope}/${t.capabilityName}`
    tasksByCapability.set(key, [...(tasksByCapability.get(key) ?? []), t])
  }
  const present = GCC_PATHWAYS.filter(p => tasks.some(t => t.pathway === p))
  const fteByPathway = present.map(p => ({ p, fte: tasks.filter(t => t.pathway === p).reduce((s, t) => s + t.baselineFte, 0) }))
  const mixedCount = rows.filter(r => r.isMixed).length
  const flow = stage3.waveFlow ?? []
  // Quick wins, agentify first (WI 2806): a capability matches when any of its tasks is one
  const quickWinCount = tasks.filter(t => t.quickWin).length
  const hasQuickWin = (cap: GccCapabilitySequencing) =>
    (tasksByCapability.get(`${cap.functionScope}/${cap.capabilityName}`) ?? []).some(t => t.quickWin)
  const filtered = rows.filter(r => matchesSequencingFilter(r, pathwayFilter, transformFilter, migrationFilter, mixedOnly) && (!quickWinOnly || hasQuickWin(r)))
  const hasFilter = !!pathwayFilter || !!transformFilter || !!migrationFilter || mixedOnly || quickWinOnly
  const defaulted = stage3.defaultedTaskIds ?? []

  return (
    <div>
      <section aria-label="Pathway summary" className="mb-4 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-xs text-gray-500">
        <span><span className="font-semibold text-gray-800">{tasks.length}</span> task{tasks.length !== 1 ? 's' : ''} sequenced</span>
        {fteByPathway.map(({ p, fte }) => (
          <span key={p}>
            <span className="font-medium" style={{ color: PATHWAY_STYLE[p].text }}>{GCC_PATHWAY_LABELS[p]}</span> {fmtFte2(fte)} FTE
          </span>
        ))}
        <span>{mixedCount} of {rows.length} capabilit{rows.length === 1 ? 'y' : 'ies'} mixed</span>
        {quickWinCount > 0 && (
          <GccTooltip content={GCC_TOOLTIPS.sequencingQuickWin}>
            <span><span className="font-semibold gx-t-good">{quickWinCount}</span> quick win{quickWinCount !== 1 ? 's' : ''} agentified first</span>
          </GccTooltip>
        )}
      </section>

      {flow.length > 0 && (
        <section aria-label="Wave roadmap" className="mb-5">
          <table className="gx-tbl w-full max-w-xl text-xs">
            <thead>
              <tr className="text-left text-gray-400 uppercase tracking-wide border-b border-gray-100">
                <th className="pb-2 font-medium"><GccTooltip content={<>{GCC_TOOLTIPS.sequencingWaveFlow}<TipEnum entries={WAVE_META} /></>}>Wave</GccTooltip></th>
                <th className="pb-2 font-medium text-right">AI transformation (FTE released)</th>
                <th className="pb-2 font-medium text-right">Migration (FTE moved)</th>
              </tr>
            </thead>
            <tbody>
              {flow.map(w => (
                <tr key={w.wave} className="border-b border-gray-50">
                  <td className="py-1.5 text-gray-700"><span className="font-semibold">Wave {w.wave}</span> <span className="text-gray-400">{GCC_WAVE_BANDS[w.wave].label}</span></td>
                  <td className="py-1.5 text-right font-semibold gx-t-ai">{fmtFte2(w.transformFte)}</td>
                  <td className="py-1.5 text-right font-semibold gx-t-loc">{fmtFte2(w.migrationFte)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <div className="flex flex-wrap items-center gap-1.5 mb-4 pb-3 border-b border-gray-100 text-xs">
        <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mr-1">
          <GccTooltip content={<>{GCC_TOOLTIPS.sequencingPathway}<TipEnum entries={PATHWAY_META} /></>}>Pathway</GccTooltip>
        </span>
        {present.map(p => (
          <Chip key={p} label={GCC_PATHWAY_LABELS[p]} active={pathwayFilter === p} style={PATHWAY_STYLE[p]} onClick={() => setPathwayFilter(x => x === p ? null : p)} />
        ))}
        <span className="text-gray-200 mx-0.5">|</span>
        <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mr-1">Transform</span>
        {GCC_WAVES.map(w => (
          <Chip key={w} label={`T-W${w}`} active={transformFilter === w} style={GCC_BADGE.purple} onClick={() => setTransformFilter(x => x === w ? null : w)} />
        ))}
        <span className="text-gray-200 mx-0.5">|</span>
        <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mr-1">Migrate</span>
        {GCC_WAVES.map(w => (
          <Chip key={w} label={`M-W${w}`} active={migrationFilter === w} style={GCC_BADGE.cyan} onClick={() => setMigrationFilter(x => x === w ? null : w)} />
        ))}
        <span className="text-gray-200 mx-0.5">|</span>
        <Chip label="Mixed only" active={mixedOnly} style={GCC_BADGE.yellow} onClick={() => setMixedOnly(x => !x)} />
        {quickWinCount > 0 && (
          <Chip label="Quick wins" active={quickWinOnly} style={GCC_BADGE.green} onClick={() => setQuickWinOnly(x => !x)} />
        )}
        {hasFilter && (
          <button onClick={() => { setPathwayFilter(null); setTransformFilter(null); setMigrationFilter(null); setMixedOnly(false); setQuickWinOnly(false) }} className="gx-link ml-1">Clear</button>
        )}
        <span className="ml-auto text-gray-400 text-[11px]">
          {hasFilter ? `${filtered.length} of ${rows.length}` : `${rows.length} rows`}
        </span>
      </div>

      <div className="gx-tbl-wrap">
        <table className="gx-tbl w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-400 border-b border-gray-100 uppercase tracking-wide">
              <th className="pb-3 font-medium w-48">Capability</th>
              <th className="pb-3 font-medium w-64">
                <GccTooltip content={<>{GCC_TOOLTIPS.sequencingPathwaySplit}<TipEnum entries={PATHWAY_META} /></>}>Pathway</GccTooltip>
              </th>
              <th className="pb-3 font-medium w-24">
                <GccTooltip content={<>{GCC_TOOLTIPS.sequencingTransformWave}<TipEnum entries={WAVE_META} /></>}>AI Transform</GccTooltip>
              </th>
              <th className="pb-3 font-medium w-24">
                <GccTooltip content={<>{GCC_TOOLTIPS.sequencingMigrationWave}<TipEnum entries={WAVE_META} /></>}>Migration</GccTooltip>
              </th>
              <th className="pb-3 font-medium w-20"><GccTooltip content={GCC_TOOLTIPS.urgencyScore}>Urgency</GccTooltip></th>
              <th className="pb-3 font-medium">Rationale</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filtered.map((cap, i) => {
              const capTasks = tasksByCapability.get(`${cap.functionScope}/${cap.capabilityName}`) ?? []
              const isOpen = openRow === i
              return (
                <Fragment key={i}>
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="py-3 align-top">
                      <div className="font-semibold text-gray-800">{cap.capabilityName}</div>
                      <div className="text-[10px] text-gray-400">{cap.functionScope}</div>
                      {cap.isMixed && (
                        <Badge label="Mixed" style={GCC_BADGE.yellow} className="mt-1" />
                      )}
                    </td>
                    <td className="py-3 align-top pr-4">
                      {cap.pathway && <PathwayPill pathway={cap.pathway} />}
                      <PathwayBar cap={cap} />
                      {cap.isMixed && (
                        <ul className="mt-1 space-y-0.5">
                          {(cap.pathwaySplit ?? []).map((s, j) => (
                            <li key={j} className="text-[11px] text-gray-500">
                              <span style={{ color: PATHWAY_STYLE[s.pathway].text }}>{GCC_PATHWAY_LABELS[s.pathway]}</span> {s.sharePct}%
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td className="py-3 align-top"><WavePill wave={cap.transformWave} /></td>
                    <td className="py-3 align-top"><WavePill wave={cap.migrationWave} /></td>
                    <td className="py-3 align-top"><UrgencyDots score={cap.urgencyScore} /></td>
                    <td className="py-3 text-xs text-gray-500 leading-relaxed align-top">
                      {cap.rationale}
                      {capTasks.length > 0 && (
                        <button onClick={() => setOpenRow(isOpen ? null : i)} aria-expanded={isOpen}
                          className="gx-link block mt-1">
                          {isOpen ? 'Hide' : 'Show'} {capTasks.length} task{capTasks.length !== 1 ? 's' : ''}
                        </button>
                      )}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={6} className="pb-3">
                        <table className="gx-tbl w-full text-xs bg-gray-50 rounded">
                          <thead>
                            <tr className="text-left text-gray-400">
                              <th className="px-3 py-1.5 font-medium">Task</th>
                              <th className="px-3 py-1.5 font-medium">Disposition</th>
                              <th className="px-3 py-1.5 font-medium">Sourcing</th>
                              <th className="px-3 py-1.5 font-medium">Pathway</th>
                              <th className="px-3 py-1.5 font-medium text-center"><GccTooltip content={GCC_TOOLTIPS.sequencingAiReadiness}>AI ready</GccTooltip></th>
                              <th className="px-3 py-1.5 font-medium text-center"><GccTooltip content={GCC_TOOLTIPS.sequencingTransitionFeasibility}>Move ready</GccTooltip></th>
                              <th className="px-3 py-1.5 font-medium text-center">T</th>
                              <th className="px-3 py-1.5 font-medium text-center">M</th>
                              <th className="px-3 py-1.5 font-medium">Rationale</th>
                            </tr>
                          </thead>
                          <tbody>
                            {capTasks.map(t => (
                              <tr key={t.taskId}>
                                <td className="px-3 py-1 text-gray-700">
                                  <span>{t.taskName}</span>
                                  {t.quickWin && t.monthsToImplement !== undefined && quickWinHorizonMonths !== undefined && (
                                    <QuickWinBadge monthsToImplement={t.monthsToImplement} horizonMonths={quickWinHorizonMonths} className="ml-1.5" />
                                  )}
                                </td>
                                <td className="px-3 py-1 text-gray-600">{t.disposition}</td>
                                <td className="px-3 py-1 text-gray-600">{formatSourcingPair(t.ownership, t.location)}</td>
                                <td className="px-3 py-1"><PathwayPill pathway={t.pathway} /></td>
                                <td className="px-3 py-1 text-center text-gray-700" title={t.defaulted ? 'Not scored validly by Step 3 — default score' : undefined}>
                                  {t.aiReadiness}{t.defaulted && <span className="ml-0.5 text-amber-600">*</span>}
                                </td>
                                <td className="px-3 py-1 text-center text-gray-700">{t.transitionFeasibility}</td>
                                <td className="px-3 py-1 text-center text-gray-700">{t.transformWave ? `W${t.transformWave}` : '—'}</td>
                                <td className="px-3 py-1 text-center text-gray-700">{t.migrationWave ? `W${t.migrationWave}` : '—'}</td>
                                <td className="px-3 py-1 text-gray-500">{t.rationale}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
      {defaulted.length > 0 && (
        <p className="mt-2 text-[10px] text-amber-700">
          {defaulted.length} task{defaulted.length !== 1 ? 's were' : ' was'} not scored validly by Step 3 and planned for Wave 3.
        </p>
      )}
    </div>
  )
}

/**
 * Sequencing tab. v2 designs (WI 2325) show task pathways rolled up per capability,
 * with separate AI transformation and migration waves; v1 designs keep the
 * verdict-and-one-wave view.
 */
export default function GccSequencing({ stage3, quickWinHorizonMonths }: {
  stage3: GccStage3Result | undefined
  /** Step 1 quick-win horizon (WI 2806), for the quick-win basis; absent on older designs */
  quickWinHorizonMonths?: number
}) {
  const rows = stage3?.sequencing ?? []
  if (!stage3 || rows.length === 0) return <p className="text-sm text-gray-400">No sequencing data.</p>
  return isV2Sequencing(stage3)
    ? <PathwaySequencing stage3={stage3} quickWinHorizonMonths={quickWinHorizonMonths} />
    : <LegacySequencing rows={rows} />
}
